import { Decimal } from '@snap-types/types/decimal';
import { z } from 'zod/v4';

import { WritableQuoteBuilderStateType } from '../../types/state';
import { buildElementId } from '../utilities/element-id';
import { buildIssueId } from '../utilities/issue-id';
import { deleteIssue, upsertIssue } from '../utilities/issue-managers';
import { parseNumberInput, validateDecimalPrecision } from './number-input';

type ValidateUnitPriceParams = {
  unitPrice: string | Decimal;
  quoteLineUuid: string;
  quotePriceUuid: string;
};

const validator = z
  .number({
    error: 'Unit price must be a number',
  })
  .positive({ message: 'Unit price must be greater than 0' });

export function validateUnitPrice(
  state: WritableQuoteBuilderStateType,
  { unitPrice, quoteLineUuid, quotePriceUuid }: ValidateUnitPriceParams,
) {
  const unitPriceString = unitPrice.toString();

  const invalidIssueId = buildIssueId({
    area: 'quotePrice',
    quoteLineUuid,
    quotePriceUuid,
    type: 'invalid',
    field: 'unitPrice',
  });

  const parsed = parseNumberInput({
    value: unitPriceString,
    requiredMessage: 'Unit price is required',
    invalidMessage: 'Unit price must be a number',
  });

  if (parsed.error) {
    return upsertIssue(state, invalidIssueId, {
      title: 'Invalid unit price',
      description: parsed.error,
      severity: 'error',
      type: 'invalid',
      field: 'unitPrice',
      area: 'quotePrice',
      quoteLineUuid,
      quotePriceUuid,
      action: {
        type: 'modify',
        elementId: buildElementId({
          uuid: quotePriceUuid,
          field: 'unitPrice',
        }),
      },
    });
  }

  const precisionError = validateDecimalPrecision({
    value: unitPriceString,
    maxDecimalPlaces: state.decimalPlaces,
    maxIntegerDigits: 15,
    decimalPlacesMessage: `Unit price cannot have more than ${state.decimalPlaces} decimal places`,
    integerDigitsMessage:
      'Unit price cannot have more than 15 digits before the decimal point',
  });

  if (precisionError) {
    return upsertIssue(state, invalidIssueId, {
      title: 'Invalid unit price',
      description: precisionError,
      severity: 'error',
      type: 'invalid',
      field: 'unitPrice',
      area: 'quotePrice',
      quoteLineUuid,
      quotePriceUuid,
      action: {
        type: 'modify',
        elementId: buildElementId({
          uuid: quotePriceUuid,
          field: 'unitPrice',
        }),
      },
    });
  }

  const result = validator.safeParse(parsed.parsed);

  if (!result.success) {
    const error = z.flattenError(result.error).formErrors[0];

    return upsertIssue(state, invalidIssueId, {
      title: 'Invalid unit price',
      description: error,
      severity: 'error',
      type: 'invalid',
      field: 'unitPrice',
      area: 'quotePrice',
      quoteLineUuid,
      quotePriceUuid,
      action: {
        type: 'modify',
        elementId: buildElementId({
          uuid: quotePriceUuid,
          field: 'unitPrice',
        }),
      },
    });
  }

  deleteIssue(state, invalidIssueId);

  return new Decimal(result.data);
}
